import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@shared/components/ui/card'
import { Button } from '@shared/components/ui/button'
import { Input } from '@shared/components/ui/input'
import { Label } from '@shared/components/ui/label'
import { Badge } from '@shared/components/ui/badge'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@shared/components/ui/dialog'
import { Textarea } from '@shared/components/ui/textarea'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@shared/components/ui/table'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shared/components/ui/select'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { 
  faPlus, 
  faEdit, 
  faCreditCard,
  faCheck,
  faTimes
} from '@fortawesome/free-solid-svg-icons'
import { toast } from '@shared/hooks/use-toast'
import { apiUrl } from '@/lib/api'
import { PageHeader } from '@shared/components/ui/page-header'

interface Plan {
  id: string
  name: string
  description: string
  price: number
  interval: 'monthly' | 'yearly'
  features: string[] | Record<string, boolean>
  is_active: boolean
  hidden: boolean
  created_at: string
}

interface CreatePlanData {
  name: string
  description: string
  price: number
  interval: 'monthly' | 'yearly'
  features: string[]
  hidden: boolean
}

// API functions
const fetchPlans = async (): Promise<Plan[]> => {
  const { auth } = await import('@/lib/firebase')
  const idToken = await auth.currentUser?.getIdToken()
  const response = await fetch(apiUrl('/api/admin/plans'), { headers: idToken ? { 'Authorization': `Bearer ${idToken}` } : {} })
  
  if (!response.ok) {
    throw new Error('Failed to fetch plans')
  }
  
  const data = await response.json()
  return data.plans || []
}

const createPlan = async (planData: CreatePlanData): Promise<Plan> => {
  const { auth } = await import('@/lib/firebase')
  const idToken = await auth.currentUser?.getIdToken()
  const response = await fetch(apiUrl('/api/admin/plans'), { method: 'POST', headers: { 'Content-Type': 'application/json', ...(idToken ? { 'Authorization': `Bearer ${idToken}` } : {}) }, body: JSON.stringify(planData) })
  
  if (!response.ok) {
    throw new Error('Failed to create plan')
  }
  
  return response.json()
}

const updatePlan = async (id: string, data: Partial<CreatePlanData & { features: Record<string, boolean> }>): Promise<void> => {
  const { auth } = await import('@/lib/firebase')
  const idToken = await auth.currentUser?.getIdToken()
  const response = await fetch(apiUrl(`/api/admin/plans/${id}`), { method: 'PUT', headers: { 'Content-Type': 'application/json', ...(idToken ? { 'Authorization': `Bearer ${idToken}` } : {}) }, body: JSON.stringify(data) })
  if (!response.ok) throw new Error('Failed to update plan')
}

const updatePlanStatus = async ({ id, isActive }: { id: string; isActive: boolean }): Promise<void> => {
  const { auth } = await import('@/lib/firebase')
  const idToken = await auth.currentUser?.getIdToken()
  const response = await fetch(apiUrl(`/api/admin/plans/${id}/status`), { method: 'POST', headers: { 'Content-Type': 'application/json', ...(idToken ? { 'Authorization': `Bearer ${idToken}` } : {}) }, body: JSON.stringify({ isActive }) })
  
  if (!response.ok) {
    throw new Error('Failed to update plan status')
  }
}

const updatePlanHidden = async ({ id, hidden }: { id: string; hidden: boolean }): Promise<void> => {
  const { auth } = await import('@/lib/firebase')
  const idToken = await auth.currentUser?.getIdToken()
  const response = await fetch(apiUrl(`/api/admin/plans/${id}/hidden`), { method: 'POST', headers: { 'Content-Type': 'application/json', ...(idToken ? { 'Authorization': `Bearer ${idToken}` } : {}) }, body: JSON.stringify({ hidden }) })
  
  if (!response.ok) {
    throw new Error('Failed to update plan hidden status')
  }
}

const PlansPage: React.FC = () => {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [newPlan, setNewPlan] = useState<CreatePlanData>({
    name: '',
    description: '',
    price: 0,
    interval: 'monthly',
    features: [],
    hidden: false
  })
  const [newFeature, setNewFeature] = useState('')
  const queryClient = useQueryClient()

  // Queries
  const { data: plans = [], isLoading, error } = useQuery({
    queryKey: ['plans'],
    queryFn: fetchPlans,
    retry: false
  })

  // Mutations
  const createPlanMutation = useMutation({
    mutationFn: createPlan,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plans'] })
      setIsCreateDialogOpen(false)
      setNewPlan({
        name: '',
        description: '',
        price: 0,
        interval: 'monthly',
        features: [],
        hidden: false
      })
      toast({
        title: 'Success',
        description: 'Plan created successfully',
      })
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      })
    }
  })

  const updateStatusMutation = useMutation({
    mutationFn: updatePlanStatus,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plans'] })
      toast({
        title: 'Success',
        description: 'Plan status updated successfully',
      })
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      })
    }
  })

  const updateHiddenMutation = useMutation({
    mutationFn: updatePlanHidden,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plans'] })
      toast({
        title: 'Success',
        description: 'Plan hidden status updated successfully',
      })
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      })
    }
  })

  const handleCreatePlan = () => {
    if (!newPlan.name || !newPlan.description || newPlan.price <= 0) {
      toast({
        title: 'Error',
        description: 'Please fill in all required fields',
        variant: 'destructive',
      })
      return
    }
    createPlanMutation.mutate(newPlan)
  }

  const handleStatusToggle = (plan: Plan) => {
    updateStatusMutation.mutate({ id: plan.id, isActive: !plan.is_active })
  }

  const handleHiddenToggle = (plan: Plan) => {
    updateHiddenMutation.mutate({ id: plan.id, hidden: !plan.hidden })
  }

  const addFeature = () => {
    if (newFeature.trim()) {
      setNewPlan({
        ...newPlan,
        features: [...newPlan.features, newFeature.trim()]
      })
      setNewFeature('')
    }
  }

  const removeFeature = (index: number) => {
    setNewPlan({
      ...newPlan,
      features: newPlan.features.filter((_, i) => i !== index)
    })
  }

  if (error) {
    return (
      <div className="space-y-6">
        <PageHeader title="Plans" description="Manage subscription plans and pricing" />
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-red-500">Error: {error.message}</p>
              <p className="text-muted-foreground mt-2">
                Please check your API key and try again
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader title="Plans" description="Manage subscription plans and pricing">
        <div className="flex items-center gap-2">
          <Button variant="default" onClick={() => setIsCreateDialogOpen(true)}>
            <FontAwesomeIcon icon={faPlus} className="mr-2 h-4 w-4" />
            Create Plan
          </Button>
        </div>
      </PageHeader>

      <div className="flex justify-between items-center">
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Create New Plan</DialogTitle>
              <DialogDescription>
                Add a new subscription plan with features and pricing.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Plan Name</Label>
                  <Input
                    id="name"
                    value={newPlan.name}
                    onChange={(e) => setNewPlan({ ...newPlan, name: e.target.value })}
                    placeholder="e.g., Professional"
                  />
                </div>
                <div>
                  <Label htmlFor="price">Price</Label>
                  <Input
                    id="price"
                    type="number"
                    value={newPlan.price}
                    onChange={(e) => setNewPlan({ ...newPlan, price: parseFloat(e.target.value) || 0 })}
                    placeholder="29.99"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={newPlan.description}
                  onChange={(e) => setNewPlan({ ...newPlan, description: e.target.value })}
                  placeholder="Describe what this plan offers..."
                />
              </div>
              <div>
                <Label htmlFor="interval">Billing Interval</Label>
                <Select
                  value={newPlan.interval}
                  onValueChange={(value) => setNewPlan({ ...newPlan, interval: value as 'monthly' | 'yearly' })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select billing interval" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="yearly">Yearly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="hidden"
                    checked={newPlan.hidden}
                    onChange={(e) => setNewPlan({ ...newPlan, hidden: e.target.checked })}
                    className="rounded"
                  />
                  <Label htmlFor="hidden">Hidden Plan (Admin-only assignable)</Label>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Hidden plans are not publicly selectable but can be assigned by admins
                </p>
              </div>
              <div>
                <Label>Features</Label>
                <div className="space-y-2">
                  <div className="flex space-x-2">
                    <Input
                      value={newFeature}
                      onChange={(e) => setNewFeature(e.target.value)}
                      placeholder="Add a feature..."
                      onKeyPress={(e) => e.key === 'Enter' && addFeature()}
                    />
                    <Button type="button" onClick={addFeature} size="sm">
                      Add
                    </Button>
                  </div>
                  <div className="space-y-1">
                    {newPlan.features.map((feature, index) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-muted rounded">
                        <span>{feature}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeFeature(index)}
                        >
                          <FontAwesomeIcon icon={faTimes} className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleCreatePlan}
                disabled={createPlanMutation.isPending}
              >
                {createPlanMutation.isPending ? 'Creating...' : 'Create Plan'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Plans</CardTitle>
            <FontAwesomeIcon icon={faCreditCard} className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{plans.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Plans</CardTitle>
            <FontAwesomeIcon icon={faCheck} className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {plans.filter(p => p.is_active).length}
            </div>
          </CardContent>
        </Card>
        {/*<Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Average Price</CardTitle>
            <FontAwesomeIcon icon={faCreditCard} className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              ${plans.length > 0 ? (plans.reduce((sum, p) => sum + p.price, 0) / plans.length).toFixed(0) : 0}
            </div>
          </CardContent>
        </Card>*/}
      </div>

      {/* Plans Table */}
      <Card>
        <CardHeader>
          <CardTitle>All Plans</CardTitle>
          <CardDescription>
            A list of all subscription plans
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground">Loading plans...</p>
            </div>
          ) : plans.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground">No plans found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Description</TableHead>
                  {/*<TableHead>Features</TableHead>*/}
                  <TableHead>Status</TableHead>
                  <TableHead>Visibility</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {plans.map((plan) => (
                  <TableRow key={plan.id}>
                    <TableCell className="font-medium">{plan.name}</TableCell>
                    <TableCell>{plan.description}</TableCell>
                    {/*
                    <TableCell>
                      <div className="space-y-1">
                        {(() => {
                          const featureList = Array.isArray(plan.features)
                            ? plan.features
                            : Object.entries(plan.features || {})
                                .filter(([, enabled]) => Boolean(enabled))
                                .map(([key]) => key)

                          return (
                            <>
                              {featureList.slice(0, 2).map((feature, index) => (
                                <Badge key={index} variant="secondary" className="text-xs">
                                  {feature}
                                </Badge>
                              ))}
                              {featureList.length > 2 && (
                                <Badge variant="outline" className="text-xs">
                                  +{featureList.length - 2} more
                                </Badge>
                              )}
                            </>
                          )
                        })()}
                      </div>
                    </TableCell>*/}
                    <TableCell>
                      <Badge variant={plan.is_active ? 'default' : 'secondary'}>
                        {plan.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={plan.hidden ? 'destructive' : 'outline'}>
                        {plan.hidden ? 'Hidden' : 'Public'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleStatusToggle(plan)}
                          disabled={updateStatusMutation.isPending}
                        >
                          {plan.is_active ? 'Deactivate' : 'Activate'}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleHiddenToggle(plan)}
                          disabled={updateHiddenMutation.isPending}
                        >
                          {plan.hidden ? 'Show' : 'Hide'}
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => window.location.assign(`/plans/${plan.id}/edit`)}>
                          <FontAwesomeIcon icon={faEdit} className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export default PlansPage
